// ── JobSpec Authoring Context ──
// Layer 3b — runtime store for spec drafts, published overlay versions,
// and per-spec change logs. Persisted to localStorage. Wraps the pure
// data layer in `jobSpecAuthoring.ts`.
//
// Resolution flow for binding NEW jobs:
//   getCanonicalSpecForArchetype(archetype)
//     → if any published overlay version exists for this spec, return the
//       highest-versioned one
//     → else return the registry canonical
// Existing BoundJobSpec snapshots are immutable — overlays do not retro-mutate.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { UserRole } from "./RoleContext";
import {
  appendLog,
  toAuthoringSpec,
  toRegistrySpec,
  summarizeChanges,
  nextVersionNumber,
  JOBSPEC_AUTHORING_STORAGE_KEY,
  resolveEffectiveCanonicalSpec,
  type AuthoringSpec,
  type JobSpecAuthoringRecord,
  type JobSpecChangeLogEntry,
} from "./jobSpecAuthoring";
import {
  getJobSpec,
  getLatestCanonicalVersion,
  type JobSpec,
} from "./jobSpec";

const STORAGE_KEY = JOBSPEC_AUTHORING_STORAGE_KEY;

interface JobSpecAuthoringContextValue {
  records: JobSpecAuthoringRecord[];
  /** Look up a draft for a specId (undefined if no draft in flight). */
  getDraft: (specId: string) => AuthoringSpec | undefined;
  /** Look up the change log for a spec (newest first). */
  getChangeLog: (specId: string) => JobSpecChangeLogEntry[];
  /** Look up published overlay versions for a spec. */
  getPublishedVersions: (specId: string) => AuthoringSpec[];
  /** Stage edits as a draft. Pure save — no version bump, no audit beyond draft_saved. */
  saveDraft: (specId: string, draft: AuthoringSpec, opts: { changedBy: UserRole }) => void;
  /** Discard current draft. */
  discardDraft: (specId: string, opts: { changedBy: UserRole }) => void;
  /**
   * Publish current draft as a new overlay version. Bumps versionNumber
   * past the registry latest AND all overlay versions. Logs `published`.
   * Throws if no draft exists.
   */
  publishDraft: (
    specId: string,
    opts: { changedBy: UserRole; reason: string },
  ) => { newVersionNumber: number };
  /**
   * Resolve the EFFECTIVE canonical spec for binding new jobs. Prefers
   * the highest-versioned overlay; falls back to registry canonical.
   */
  resolveEffectiveCanonical: (
    base: JobSpec | undefined,
  ) => JobSpec | undefined;
}

const JobSpecAuthoringContext = createContext<JobSpecAuthoringContextValue | null>(null);

function loadRecords(): JobSpecAuthoringRecord[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as JobSpecAuthoringRecord[]) : [];
  } catch {
    return [];
  }
}

export function JobSpecAuthoringProvider({ children }: { children: ReactNode }) {
  const [records, setRecords] = useState<JobSpecAuthoringRecord[]>(loadRecords);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    } catch {
      /* quota / disabled — silent */
    }
  }, [records]);

  const upsertRecord = useCallback(
    (specId: string, mut: (r: JobSpecAuthoringRecord) => JobSpecAuthoringRecord) => {
      setRecords((prev) => {
        const idx = prev.findIndex((r) => r.specId === specId);
        const base: JobSpecAuthoringRecord = idx >= 0
          ? prev[idx]
          : { specId, draft: undefined, publishedVersions: [], changeLog: [] };
        const next = mut(base);
        if (idx >= 0) {
          const out = prev.slice();
          out[idx] = next;
          return out;
        }
        return [...prev, next];
      });
    },
    [],
  );

  const getDraft = useCallback(
    (specId: string) => records.find((r) => r.specId === specId)?.draft,
    [records],
  );

  const getChangeLog = useCallback(
    (specId: string) => records.find((r) => r.specId === specId)?.changeLog ?? [],
    [records],
  );

  const getPublishedVersions = useCallback(
    (specId: string) => records.find((r) => r.specId === specId)?.publishedVersions ?? [],
    [records],
  );

  const saveDraft = useCallback<JobSpecAuthoringContextValue["saveDraft"]>(
    (specId, draft, opts) => {
      upsertRecord(specId, (rec) => {
        const baseline =
          rec.draft ??
          (rec.publishedVersions.length > 0
            ? rec.publishedVersions[rec.publishedVersions.length - 1]
            : (() => {
                // Look up the registry canonical via specId+latest version
                const latest = getLatestCanonicalVersion(specId);
                const reg = latest !== undefined ? getJobSpec(specId, latest) : undefined;
                return reg ? toAuthoringSpec(reg) : draft;
              })());
        const summary = summarizeChanges(baseline, draft);
        const entry: JobSpecChangeLogEntry = {
          id: `jsl_${Date.now()}`,
          changedAt: new Date().toISOString(),
          changedBy: opts.changedBy,
          summary,
          action: "draft_saved",
        };
        return {
          ...rec,
          draft,
          changeLog: appendLog(rec.changeLog, entry),
        };
      });
    },
    [upsertRecord],
  );

  const discardDraft = useCallback<JobSpecAuthoringContextValue["discardDraft"]>(
    (specId, opts) => {
      upsertRecord(specId, (rec) => {
        if (!rec.draft) return rec;
        const entry: JobSpecChangeLogEntry = {
          id: `jsl_${Date.now()}`,
          changedAt: new Date().toISOString(),
          changedBy: opts.changedBy,
          summary: "draft discarded",
          action: "discarded",
        };
        return { ...rec, draft: undefined, changeLog: appendLog(rec.changeLog, entry) };
      });
    },
    [upsertRecord],
  );

  const publishDraft = useCallback<JobSpecAuthoringContextValue["publishDraft"]>(
    (specId, opts) => {
      let publishedVersionNumber = 0;
      upsertRecord(specId, (rec) => {
        if (!rec.draft) {
          throw new Error(`Cannot publish ${specId}: no draft.`);
        }
        const registryLatest = getLatestCanonicalVersion(specId) ?? 0;
        const newVersion = nextVersionNumber(registryLatest, rec.publishedVersions);
        publishedVersionNumber = newVersion;
        const published: AuthoringSpec = {
          ...rec.draft,
          versionNumber: newVersion,
          status: "canonical",
        };
        const entry: JobSpecChangeLogEntry = {
          id: `jsl_${Date.now()}`,
          changedAt: new Date().toISOString(),
          changedBy: opts.changedBy,
          summary: `published v${newVersion}`,
          reason: opts.reason,
          action: "published",
          publishedVersion: newVersion,
        };
        return {
          ...rec,
          draft: undefined,
          publishedVersions: [...rec.publishedVersions, published],
          changeLog: appendLog(rec.changeLog, entry),
        };
      });
      return { newVersionNumber: publishedVersionNumber };
    },
    [upsertRecord],
  );

  const resolveEffectiveCanonical = useCallback<
    JobSpecAuthoringContextValue["resolveEffectiveCanonical"]
  >(
    (base) => resolveEffectiveCanonicalSpec(base, records),
    [records],
  );

  const value = useMemo(
    () => ({
      records,
      getDraft,
      getChangeLog,
      getPublishedVersions,
      saveDraft,
      discardDraft,
      publishDraft,
      resolveEffectiveCanonical,
    }),
    [
      records,
      getDraft,
      getChangeLog,
      getPublishedVersions,
      saveDraft,
      discardDraft,
      publishDraft,
      resolveEffectiveCanonical,
    ],
  );

  return (
    <JobSpecAuthoringContext.Provider value={value}>
      {children}
    </JobSpecAuthoringContext.Provider>
  );
}

export function useJobSpecAuthoring(): JobSpecAuthoringContextValue {
  const ctx = useContext(JobSpecAuthoringContext);
  if (!ctx) {
    // No-op fallback for code paths outside the provider (tests, storybook).
    return {
      records: [],
      getDraft: () => undefined,
      getChangeLog: () => [],
      getPublishedVersions: () => [],
      saveDraft: () => {},
      discardDraft: () => {},
      publishDraft: () => ({ newVersionNumber: 0 }),
      resolveEffectiveCanonical: (base) => base,
    };
  }
  return ctx;
}
